import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import FileThumbnails from "../../../Estimates/FileThumbnails";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";
import {
  getMuestraFileKey,
  isPresentacionMuestraFile,
  isMuestraVideoFile,
} from "../../../../utils/muestraFotosExport";
import { downloadFileFromMuestra } from "../../../../api/Muestras/muestraApi";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import axios from "axios";

const PHOTO_REORDER_MIME = "application/x-muestra-photo-reorder";

export default function HeaderSection({
  muestra,
  isEditMode,
  canDeletePhotos = false,
  confirmDelete,
  onPrintEtiqueta,
  muestraFiles = [],
  fotosExport,
  onReorderPhotos,
  onSetPhotoRole,
  onDeleteFile,
  onUploadFile,
  onPreparePhotos,
  preparingPhotos = false,
  preparePhotoProgress = null,
  onPreviewCleanPhotos,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [videoPreview, setVideoPreview] = useState({
    open: false,
    name: "",
    url: null,
    loading: false,
    error: null,
  });

  const canUpload = Boolean(muestra?.QR_CODE || muestra?.ID_MUESTRA);
  const canEditPhotos = Boolean(
    (isEditMode || muestra?.ID_MUESTRA) && muestra?.ID_MUESTRA,
  );
  const allowDeletePhotos = Boolean(
    (canDeletePhotos || isEditMode) && muestra?.ID_MUESTRA,
  );

  useEffect(() => {
    return () => {
      if (videoPreview.url) URL.revokeObjectURL(videoPreview.url);
    };
  }, [videoPreview.url]);

  const handleDownloadFile = async (file) => {
    if (!muestra?.ID_MUESTRA || !file?.name) return;
    const key = getMuestraFileKey(file);
    setDownloadingKey(key);
    try {
      await downloadFileFromMuestra(
        muestra.ID_MUESTRA,
        file.name,
        file.category,
      );
    } catch (err) {
      console.error("Error descargando archivo de muestra:", err);
    } finally {
      setDownloadingKey(null);
    }
  };

  const openVideoPreview = async (file) => {
    if (!muestra?.ID_MUESTRA || !file?.name) return;
    if (videoPreview.url) URL.revokeObjectURL(videoPreview.url);
    setVideoPreview({
      open: true,
      name: file.name,
      url: null,
      loading: true,
      error: null,
    });
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/files/muestra/${muestra.ID_MUESTRA}/download/${encodeURIComponent(file.name)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: file.category ? { category: file.category } : undefined,
          responseType: "blob",
        },
      );
      const mime =
        file.mimeType || response.headers?.["content-type"] || "video/mp4";
      const blob =
        response.data instanceof Blob
          ? response.data.type
            ? response.data
            : new Blob([response.data], { type: mime })
          : new Blob([response.data], { type: mime });
      setVideoPreview({
        open: true,
        name: file.name,
        url: URL.createObjectURL(blob),
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error("Error abriendo vídeo de muestra:", err);
      setVideoPreview({
        open: true,
        name: file.name,
        url: null,
        loading: false,
        error: t("Samples.videoOpenError", {
          defaultValue: "No se pudo abrir el vídeo",
        }),
      });
    }
  };

  const closeVideoPreview = () => {
    if (videoPreview.url) URL.revokeObjectURL(videoPreview.url);
    setVideoPreview({
      open: false,
      name: "",
      url: null,
      loading: false,
      error: null,
    });
  };

  const isReorderDrag = (e) =>
    Array.from(e.dataTransfer?.types || []).includes(PHOTO_REORDER_MIME);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!canUpload || isReorderDrag(e)) return;

    const dropped = Array.from(e.dataTransfer?.files || []).filter((f) =>
      f.type?.startsWith("image/"),
    );
    if (dropped.length) onUploadFile?.(dropped);
  };

  const handlePhotoDragStart = (e, index) => {
    if (!canEditPhotos) return;
    e.stopPropagation();
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(PHOTO_REORDER_MIME, String(index));
  };

  const handlePhotoDragOver = (e, index) => {
    if (!canEditPhotos || draggingIndex == null) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dropTargetIndex !== index) setDropTargetIndex(index);
  };

  const handlePhotoDrop = (e, toIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIndex = draggingIndex;
    setDraggingIndex(null);
    setDropTargetIndex(null);
    if (fromIndex == null || fromIndex === toIndex) return;
    onReorderPhotos?.(fromIndex, toIndex);
  };

  const handlePhotoDragEnd = () => {
    setDraggingIndex(null);
    setDropTargetIndex(null);
  };

  const dragHandlers = canUpload
    ? {
        onDragEnter: (e) => {
          if (draggingIndex != null || isReorderDrag(e)) return;
          e.preventDefault();
          e.stopPropagation();
          setDragOver(true);
        },
        onDragOver: (e) => {
          if (draggingIndex != null || isReorderDrag(e)) return;
          e.preventDefault();
          e.stopPropagation();
          setDragOver(true);
        },
        onDragLeave: (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
        },
        onDrop: handleDrop,
      }
    : {};

  const iconOutlineSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    p: "3px",
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      borderColor: "primary.main",
    },
  };

  const frontKey = fotosExport?.front || null;
  const backKey = fotosExport?.back || null;
  const cleanPhotos = muestraFiles.filter(isPresentacionMuestraFile);

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        px: 1.75,
        pt: 1.1,
        pb: 1.1,
        borderBottom: "1px solid",
        borderColor: "divider",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 70%)`,
        backdropFilter: "blur(8px)",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1.5}
      >
        {muestra?.QR_CODE ? (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box sx={{ textAlign: "center", flexShrink: 0 }}>
              <img
                src={muestra.QR_CODE}
                alt={t("Samples.qrCodeAlt")}
                style={{
                  width: 72,
                  height: 72,
                  border: `1px solid ${theme.palette.divider}`,
                  padding: 3,
                  borderRadius: 10,
                  backgroundColor: theme.palette.background.paper,
                  display: "block",
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {muestraFiles.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 0.35,
                    color: "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  {cleanPhotos.length > 0
                    ? t("Samples.cleanPhotosHint", {
                        count: cleanPhotos.length,
                      })
                    : t("Samples.pdfPhotosHint")}
                  {cleanPhotos.length > 0 && (
                    <>
                      {" · "}
                      <Box
                        component="span"
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                        onClick={() =>
                          onPreviewCleanPhotos?.(
                            cleanPhotos.map((file) => ({
                              path: getMuestraFileKey(file),
                              role:
                                frontKey === getMuestraFileKey(file)
                                  ? "front"
                                  : backKey === getMuestraFileKey(file)
                                    ? "back"
                                    : "clean",
                            })),
                          )
                        }
                      >
                        {t("Samples.viewCleanPhotos")}
                      </Box>
                    </>
                  )}
                </Typography>
              )}
              <Box
                {...dragHandlers}
                sx={{
                  display: "flex",
                  gap: 0.75,
                  overflowX: "auto",
                  overflowY: "hidden",
                  pb: 0.25,
                  minWidth: 0,
                  minHeight: 88,
                  alignItems: "center",
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 2,
                  border: canUpload ? "1px dashed" : "none",
                  borderColor: dragOver
                    ? "primary.main"
                    : alpha(theme.palette.divider, 0.9),
                  bgcolor: dragOver
                    ? alpha(theme.palette.primary.main, 0.1)
                    : canUpload
                      ? alpha(theme.palette.primary.main, 0.03)
                      : "transparent",
                  transition:
                    "border-color 0.15s ease, background-color 0.15s ease",
                }}
              >
                {muestraFiles.length === 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: dragOver ? "primary.main" : "text.secondary",
                      fontStyle: "italic",
                      fontWeight: dragOver ? 600 : 400,
                    }}
                  >
                    {canUpload
                      ? dragOver
                        ? t("Samples.dropOrUploadPhoto")
                        : t("Samples.dropPhotosHint")
                      : t("Samples.noFiles")}
                  </Typography>
                )}

                {muestraFiles.map((file, i) => {
                  const fileKey = getMuestraFileKey(file);
                  const isFront = frontKey === fileKey;
                  const isBack = backKey === fileKey;
                  const isDragging = draggingIndex === i;
                  const isClean = isPresentacionMuestraFile(file);
                  const isVideo = isMuestraVideoFile(file);
                  const isDropTarget =
                    dropTargetIndex === i &&
                    draggingIndex != null &&
                    !isDragging;

                  return (
                    <Box
                      key={fileKey || file.name + i}
                      onDragOver={(e) => handlePhotoDragOver(e, i)}
                      onDrop={(e) => handlePhotoDrop(e, i)}
                      sx={{
                        border: "1px solid",
                        borderColor: isDropTarget
                          ? "primary.main"
                          : isClean
                            ? "success.main"
                            : isFront || isBack
                              ? "primary.main"
                              : "divider",
                        p: 0.6,
                        borderRadius: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.35,
                        bgcolor: isDropTarget
                          ? alpha(theme.palette.primary.main, 0.12)
                          : isClean
                            ? alpha(theme.palette.success.main, 0.08)
                            : theme.palette.background.paper,
                        flexShrink: 0,
                        width: 78,
                        opacity: isDragging ? 0.45 : 1,
                        transform: isDropTarget ? "scale(1.03)" : "none",
                        transition:
                          "opacity 0.15s ease, transform 0.15s ease, background-color 0.15s ease",
                        boxShadow:
                          isFront || isBack || isDropTarget
                            ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`
                            : "none",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.25,
                          alignItems: "center",
                          width: "100%",
                          justifyContent: "space-between",
                        }}
                      >
                        <Chip
                          size="small"
                          label={isClean ? "✓" : i + 1}
                          color={isClean ? "success" : "default"}
                          sx={{
                            height: 18,
                            minWidth: 22,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            "& .MuiChip-label": { px: 0.6 },
                          }}
                        />
                        <Box sx={{ display: "flex", gap: 0.15 }}>
                          <IconButton
                            size="small"
                            sx={iconOutlineSx}
                            disabled={
                              !muestra?.ID_MUESTRA || downloadingKey === fileKey
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadFile(file);
                            }}
                          >
                            {downloadingKey === fileKey ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <DownloadIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>

                          <IconButton
                            size="small"
                            disabled={!allowDeletePhotos}
                            sx={{
                              ...iconOutlineSx,
                              borderColor: alpha(
                                theme.palette.error.main,
                                0.45,
                              ),
                              color: "error.main",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                borderColor: "error.main",
                              },
                            }}
                            onClick={() => onDeleteFile?.(file)}
                          >
                            <DeleteOutlinedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box
                        draggable={canEditPhotos && !isClean}
                        onDragStart={(e) => handlePhotoDragStart(e, i)}
                        onDragEnd={handlePhotoDragEnd}
                        onClick={() => {
                          if (isClean) {
                            onPreviewCleanPhotos?.([
                              {
                                path: fileKey,
                                role: isFront
                                  ? "front"
                                  : isBack
                                    ? "back"
                                    : "clean",
                              },
                            ]);
                          }
                        }}
                        sx={{
                          width: 52,
                          height: 52,
                          overflow: "hidden",
                          borderRadius: 1,
                          position: "relative",
                          cursor:
                            canEditPhotos && !isClean
                              ? "grab"
                              : isClean
                                ? "zoom-in"
                                : "default",
                          "&:active": {
                            cursor: canEditPhotos ? "grabbing" : "default",
                          },
                          "& img, & canvas, & > *": {
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "cover",
                            pointerEvents: "none",
                          },
                        }}
                      >
                        {canEditPhotos && !isVideo && (
                          <DragIndicatorIcon
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              fontSize: 14,
                              color: "common.white",
                              opacity: 0.85,
                              filter: "drop-shadow(0 0 1px rgba(0,0,0,0.8))",
                              pointerEvents: "none",
                              zIndex: 1,
                            }}
                          />
                        )}
                        {isVideo ? (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              openVideoPreview(file);
                            }}
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.25,
                              cursor: "pointer",
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "grey.800"
                                  : "grey.200",
                            }}
                          >
                            <VideocamOutlinedIcon
                              sx={{ fontSize: 22, color: "text.secondary" }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 800,
                                fontSize: "0.6rem",
                                color: "text.secondary",
                                lineHeight: 1,
                              }}
                            >
                              VIDEO
                            </Typography>
                          </Box>
                        ) : (
                          <FileThumbnails filePath={file.path} />
                        )}
                      </Box>

                      {canEditPhotos && !isVideo && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.25,
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Tooltip title={t("Samples.pdfPhotoFront")}>
                            <Chip
                              size="small"
                              label="F"
                              clickable
                              color={isFront ? "primary" : "default"}
                              variant={isFront ? "filled" : "outlined"}
                              onClick={() => onSetPhotoRole?.(file, "front")}
                              sx={{
                                height: 20,
                                flex: 1,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                              }}
                            />
                          </Tooltip>
                          <Tooltip title={t("Samples.pdfPhotoBack")}>
                            <Chip
                              size="small"
                              label="B"
                              clickable
                              color={isBack ? "primary" : "default"}
                              variant={isBack ? "filled" : "outlined"}
                              onClick={() => onSetPhotoRole?.(file, "back")}
                              sx={{
                                height: 20,
                                flex: 1,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                              }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        <Box
          display="flex"
          alignItems="flex-end"
          gap={0.75}
          flexDirection="column"
          flexShrink={0}
        >
          <Typography
            variant="overline"
            sx={{ letterSpacing: 1, opacity: 0.75, lineHeight: 1, mb: -0.25 }}
          >
            {t("Samples.sampleSingular")}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              lineHeight: 1.2,
              textAlign: "right",
            }}
          >
            {isEditMode ? t("Samples.editSample") : t("Samples.addSample")}
          </Typography>
          {muestra?.MUESTRA ? (
            <Chip
              size="small"
              label={muestra.MUESTRA}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.68rem",
                maxWidth: 180,
              }}
            />
          ) : null}
          <Box display="flex" alignItems="center" gap={0.75}>
            {muestra?.QR_CODE && (
              <>
                <input
                  accept="image/*,video/*"
                  style={{ display: "none" }}
                  id="upload-muestra-drawer"
                  type="file"
                  multiple
                  onChange={onUploadFile}
                />

                <label htmlFor="upload-muestra-drawer">
                  <Tooltip title={t("Samples.dropOrUploadPhoto")}>
                    <IconButton
                      component="span"
                      size="small"
                      sx={{
                        ...iconOutlineSx,
                        p: 0.75,
                        color: "primary.main",
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      <UploadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </label>

                <Tooltip
                  title={
                    preparingPhotos && preparePhotoProgress
                      ? t("Samples.preparePhotosProgress", {
                          current: preparePhotoProgress.current,
                          total: preparePhotoProgress.total,
                        })
                      : t("Samples.preparePhotosAiHint")
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={
                        !isEditMode ||
                        preparingPhotos ||
                        muestraFiles.length === 0
                      }
                      onClick={(e) =>
                        onPreparePhotos?.(
                          e.shiftKey
                            ? "rembg"
                            : e.altKey
                              ? "gpt-image"
                              : "garment",
                        )
                      }
                      sx={{
                        ...iconOutlineSx,
                        p: 0.75,
                        color: "secondary.main",
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      {preparingPhotos ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <AutoAwesomeIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={t("Samples.printLabelQr")}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => {
                      onPrintEtiqueta();
                    }}
                    sx={{
                      "&.MuiIconButton-root": { padding: "6px" },
                      ...iconOutlineSx,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <LocalPrintshopOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Tooltip title={t("Samples.deleteSample")}>
              <span>
                <IconButton
                  disabled={!isEditMode}
                  size="small"
                  onClick={() => confirmDelete(muestra?.ID_MUESTRA)}
                  sx={{
                    "&.MuiIconButton-root": { padding: "6px" },
                    ...iconOutlineSx,
                    backgroundColor: theme.palette.background.paper,
                    borderColor: alpha(theme.palette.error.main, 0.45),
                    color: "error.main",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.error.main, 0.08),
                    },
                  }}
                >
                  <DeleteOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={videoPreview.open}
        onClose={closeVideoPreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            component="span"
            sx={{ fontWeight: 800 }}
          >
            {videoPreview.name || "VIDEO"}
          </Typography>
          <IconButton aria-label="close" onClick={closeVideoPreview}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {videoPreview.loading ? (
            <Box sx={{ display: "grid", placeItems: "center", minHeight: 220 }}>
              <CircularProgress />
            </Box>
          ) : videoPreview.error ? (
            <Typography color="error">{videoPreview.error}</Typography>
          ) : videoPreview.url ? (
            <Box
              component="video"
              src={videoPreview.url}
              controls
              autoPlay
              playsInline
              sx={{
                width: "100%",
                maxHeight: "70vh",
                bgcolor: "black",
                borderRadius: 1.5,
                display: "block",
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
